import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { projectsApi, tasksApi, usersApi, timelineApi } from "../lib/api";
import { DeadlinePhasesBadge } from "../components/DeadlinePhases";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import ArtistSelector from "../components/ArtistSelector";
import TaskTimeline from "../components/TaskTimeline";
import SupportHubProjectLink from "../components/SupportHubProjectLink";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  Plus,
  Users,
  DollarSign,
  Calendar,
  Trash2,
  UserPlus,
  Info,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";

// Max artist share percentage (business rule) - default, will be overridden by settings
const MAX_ARTIST_SHARE_PERCENT = 35;

const ProjectDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isArtDirector = user?.role === "art_director";
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [artists, setArtists] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [budgetDialogOpen, setBudgetDialogOpen] = useState(false);
  const [adAssignDialogOpen, setAdAssignDialogOpen] = useState(false);
  const [selectedAdId, setSelectedAdId] = useState("");
  const [savingAd, setSavingAd] = useState(false);
  const [budget, setBudget] = useState("");
  const [selectedArtistData, setSelectedArtistData] = useState(null);
  const [marginSettings, setMarginSettings] = useState({ warning_threshold_percent: 40, critical_threshold_percent: 50, max_artist_share_percent: 30 });
  const [serviceTypes, setServiceTypes] = useState([]);  // Dynamic service types from settings
  const [taskTimelines, setTaskTimelines] = useState({}); // {task_id: timelineProgress}
  const [taskForm, setTaskForm] = useState({
    name: "",
    task_type: "",
    service_type_id: "",  // Reference to service_types
    description: "",
    brief: "",
    brief_link: "",
    deadline: "",
    assigned_artist_id: "",
    agreed_price: 0,
    client_price: 0,
    currency: "USD",
    internal_notes: "",
    project_id: id,
    art_director_review: false,
    art_director_id: "",
  });
  const [artDirectors, setArtDirectors] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [addUserType, setAddUserType] = useState("team");

  // Calculate suggested price and margin status
  // Uses max_artist_share_percent from settings and base_price from service type for reference
  const calculatePricing = (clientPrice, taskType, serviceTypeId, agreedPrice, artDirectorFee = 0) => {
    if (!clientPrice || clientPrice <= 0) {
      return { suggestedPrice: 0, artistSharePercent: 0, marginStatus: "OK" };
    }
    
    // Find the selected service type to get base_price for reference
    const selectedServiceType = serviceTypes.find(
      st => st.id === serviceTypeId || st.name === taskType
    );
    
    // Use max_artist_share_percent from margin settings as the coefficient
    const coefficient = marginSettings.max_artist_share_percent / 100;
    
    // Recommended price = client price * max artist share percentage
    const recommendedPrice = clientPrice * coefficient;
    // Max allowed price based on settings (same as recommended in this case)
    const maxPrice = clientPrice * coefficient;
    // Final suggested price
    const suggestedPrice = recommendedPrice;
    
    // base_price from service type (absolute price in currency, e.g., $600 for Exterior Rendering)
    // This can be used as a reference for whether the client price is reasonable
    const serviceBasePrice = selectedServiceType?.base_price || null;
    
    // Calculate artist share percentage (including art director fee if applicable)
    const totalArtistCost = agreedPrice + artDirectorFee;
    const artistSharePercent = totalArtistCost > 0 ? (totalArtistCost / clientPrice) * 100 : 0;
    
    // Determine margin status based on settings
    let marginStatus = "OK";
    if (artistSharePercent >= marginSettings.critical_threshold_percent) {
      marginStatus = "CRITICAL";
    } else if (artistSharePercent >= marginSettings.warning_threshold_percent) {
      marginStatus = "WARNING";
    }
    
    return { suggestedPrice, artistSharePercent, marginStatus, recommendedPrice, maxPrice, coefficient, serviceBasePrice };
  };

  // Calculate art director fee
  const artDirectorFee = taskForm.art_director_review && taskForm.art_director_id 
    ? (taskForm.agreed_price || 0) * 0.3 
    : 0;

  const pricing = calculatePricing(taskForm.client_price, taskForm.task_type, taskForm.service_type_id, taskForm.agreed_price, artDirectorFee);

  useEffect(() => {
    fetchData();
    fetchServiceTypes();
  }, [id]);

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get("/service-types/list");
      const types = res.data || [];
      setServiceTypes(types);
      // Set default task_type if not set
      if (types.length > 0 && !taskForm.task_type) {
        setTaskForm(prev => ({
          ...prev,
          task_type: types[0].name,
          service_type_id: types[0].id
        }));
      }
    } catch (err) {
      console.error("Failed to load service types", err);
    }
  };

  const fetchData = async () => {
    try {
      const [projectRes, tasksRes] = await Promise.all([
        projectsApi.getOne(id),
        tasksApi.getAll({ project_id: id }),
      ]);
      setProject(projectRes.data);
      setTasks(tasksRes.data);
      setBudget(projectRes.data.budget?.toString() || "");

      // Load timelines for all tasks
      if (tasksRes.data && tasksRes.data.length > 0) {
        const timelinePromises = tasksRes.data.map(task => 
          timelineApi.getTaskTimeline(task.id).catch(() => null)
        );
        const timelineResults = await Promise.all(timelinePromises);
        const timelinesMap = {};
        tasksRes.data.forEach((task, idx) => {
          if (timelineResults[idx]?.data) {
            timelinesMap[task.id] = timelineResults[idx].data;
          }
        });
        setTaskTimelines(timelinesMap);
      }

      if (user?.role === "admin" || user?.role === "team" || user?.role === "pm_lead") {
        const [artistsRes, teamRes, adRes, marginRes] = await Promise.all([
          usersApi.getAll("artist"),
          usersApi.getAll("team"),
          api.get("/art-directors"),
          api.get("/settings/margin"),
        ]);
        setArtists(artistsRes.data);
        setTeamMembers(teamRes.data);
        setArtDirectors(adRes.data || []);
        if (marginRes.data) {
          setMarginSettings(marginRes.data);
        }
      }
    } catch (error) {
      console.error("Error fetching project:", error);
      toast.error("Failed to load project");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    
    // Check margin - don't allow CRITICAL margin tasks
    if (pricing.marginStatus === "CRITICAL") {
      toast.error(`Cannot create task: Artist share exceeds ${marginSettings.critical_threshold_percent}% (critical threshold)`);
      return;
    }
    
    try {
      // Calculate art director fee
      let artDirectorFee = 0;
      if (taskForm.art_director_review && taskForm.art_director_id) {
        artDirectorFee = (taskForm.agreed_price || 0) * 0.3;
      }
      
      await tasksApi.create({
        ...taskForm,
        client_price: taskForm.client_price || 0,
        brief_link: taskForm.brief_link || undefined,
        currency: taskForm.currency || "USD",
        art_director_review: taskForm.art_director_review,
        art_director_id: taskForm.art_director_review ? taskForm.art_director_id : undefined,
        art_director_fee: artDirectorFee,
        service_type_id: taskForm.service_type_id || undefined,
      });
      toast.success("Task created successfully");
      setTaskDialogOpen(false);
      setSelectedArtistData(null);
      // Reset form with first service type if available
      const defaultType = serviceTypes.length > 0 ? serviceTypes[0] : null;
      setTaskForm({
        name: "",
        task_type: defaultType?.name || "",
        service_type_id: defaultType?.id || "",
        description: "",
        brief: "",
        brief_link: "",
        deadline: "",
        assigned_artist_id: "",
        agreed_price: 0,
        client_price: 0,
        currency: "USD",
        internal_notes: "",
        project_id: id,
        art_director_review: false,
        art_director_id: "",
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create task");
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId) return;
    try {
      if (addUserType === "team") {
        await projectsApi.addTeamMember(id, selectedUserId);
        toast.success("Team member added");
      } else {
        await projectsApi.addArtist(id, selectedUserId);
        toast.success("Artist added");
      }
      setTeamDialogOpen(false);
      setSelectedUserId("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add user");
    }
  };

  const handleAssignArtDirector = async () => {
    try {
      setSavingAd(true);
      // Empty selection → clear assignment (null on backend)
      const newId = selectedAdId === "__none__" ? null : (selectedAdId || null);
      await api.put(`/unified-projects/${id}`, { art_director_id: newId });
      toast.success(newId ? "Art Director assigned" : "Art Director cleared");
      setAdAssignDialogOpen(false);
      setSelectedAdId("");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign Art Director");
    } finally {
      setSavingAd(false);
    }
  };

  const handleUpdateBudget = async () => {
    try {
      await projectsApi.updateBudget(id, parseFloat(budget) || 0);
      toast.success("Budget updated");
      setBudgetDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to update budget");
    }
  };

  const handleDeleteProject = async () => {
    try {
      await projectsApi.delete(id);
      toast.success("Project deleted");
      navigate("/projects");
    } catch (error) {
      toast.error("Failed to delete project");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  if (!project) return null;

  return (
    <div data-testid="project-detail-page">
      {/* Back Button */}
      <Link
        to="/projects"
        className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-50 transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to Projects</span>
      </Link>

      {/* Support Hub auto-link banner — appears only for projects whose
          services include Spinhouse/Vexos. Banner self-hides when project
          isn't eligible or user can't act. */}
      <SupportHubProjectLink project={project} />

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6 mb-8">
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-zinc-50 font-['Manrope'] mb-2">{project.name}</h1>
          {project.description && <p className="text-zinc-400 max-w-2xl">{project.description}</p>}
          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm text-zinc-500">
            {project.client_name && (
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span>Client: {project.client_name}</span>
              </div>
            )}
            {project.project_manager_name && (
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4" />
                <span>PM: {project.project_manager_name}</span>
              </div>
            )}
            <div className="flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-purple-400" />
              <span data-testid="project-art-director">
                Art Director: <span className={project.art_director_name ? "text-purple-300" : "text-zinc-600 italic"}>
                  {project.art_director_name || "not assigned"}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              <span>Created {new Date(project.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {(user?.role === "admin" || user?.role === "pm_lead") && (
          <div className="flex flex-wrap gap-3">
            {!isArtDirector && (
              <Dialog open={budgetDialogOpen} onOpenChange={setBudgetDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="set-budget-btn">
                  <DollarSign className="h-4 w-4 mr-2" />
                  Set Budget
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-zinc-50">Set Project Budget</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Budget Amount ($)</Label>
                    <Input
                      type="number"
                      value={budget}
                      onChange={(e) => setBudget(e.target.value)}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                      placeholder="0.00"
                      data-testid="budget-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setBudgetDialogOpen(false)} className="border-zinc-700">
                      Cancel
                    </Button>
                    <Button onClick={handleUpdateBudget} className="bg-orange-500 hover:bg-orange-600" data-testid="save-budget-btn">
                      Save Budget
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            )}

            <Dialog
              open={adAssignDialogOpen}
              onOpenChange={(open) => {
                setAdAssignDialogOpen(open);
                if (open) {
                  setSelectedAdId(project.art_director_id || "__none__");
                }
              }}
            >
              <DialogTrigger asChild>
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="assign-ad-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Art Director
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-zinc-50">Assign Art Director</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Art Director</Label>
                    <Select value={selectedAdId} onValueChange={setSelectedAdId}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50" data-testid="select-art-director">
                        <SelectValue placeholder="Select an Art Director" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="__none__" className="text-zinc-400 italic">— Not assigned —</SelectItem>
                        {artDirectors.map((ad) => (
                          <SelectItem key={ad.id} value={ad.id} className="text-zinc-300">
                            {ad.name}{ad.email ? ` (${ad.email})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-zinc-500">
                      Only Admin and PM Lead can change the project's Art Director.
                    </p>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setAdAssignDialogOpen(false)} className="border-zinc-700">
                      Cancel
                    </Button>
                    <Button
                      onClick={handleAssignArtDirector}
                      disabled={savingAd}
                      className="bg-orange-500 hover:bg-orange-600"
                      data-testid="save-art-director-btn"
                    >
                      {savingAd ? "Saving..." : "Save"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <Dialog open={teamDialogOpen} onOpenChange={setTeamDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800" data-testid="add-member-btn">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add Member
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800">
                <DialogHeader>
                  <DialogTitle className="text-zinc-50">Add Team Member or Artist</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">User Type</Label>
                    <Select value={addUserType} onValueChange={setAddUserType}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        <SelectItem value="team" className="text-zinc-300">Team Member</SelectItem>
                        <SelectItem value="artist" className="text-zinc-300">Artist</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Select User</Label>
                    <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                      <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50" data-testid="select-user">
                        <SelectValue placeholder="Select a user" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800">
                        {(addUserType === "team" ? teamMembers : artists).map((u) => (
                          <SelectItem key={u.id} value={u.id} className="text-zinc-300">
                            {u.name} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button variant="outline" onClick={() => setTeamDialogOpen(false)} className="border-zinc-700">
                      Cancel
                    </Button>
                    <Button onClick={handleAddUser} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-add-user-btn">
                      Add User
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {user?.role === "admin" && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="border-red-900 text-red-400 hover:bg-red-900/20" data-testid="delete-project-btn">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent className="bg-zinc-900 border-zinc-800">
                <AlertDialogHeader>
                  <AlertDialogTitle className="text-zinc-50">Delete Project?</AlertDialogTitle>
                  <AlertDialogDescription className="text-zinc-400">
                    This will permanently delete the project and all its tasks. This action cannot be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    Cancel
                  </AlertDialogCancel>
                  <AlertDialogAction
                    onClick={handleDeleteProject}
                    className="bg-red-600 hover:bg-red-700 text-white"
                    data-testid="confirm-delete-btn"
                  >
                    Delete Project
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className={cn("grid gap-4 mb-8", isArtDirector ? "grid-cols-2" : "grid-cols-2 md:grid-cols-4")}>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Tasks</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1">{tasks.length}</p>
          </CardContent>
        </Card>
        {!isArtDirector && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Budget</p>
              <p className="text-2xl font-bold text-zinc-50 mt-1">${(project.budget || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
        )}
        {!isArtDirector && (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4">
              <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Spent</p>
              <p className="text-2xl font-bold text-zinc-50 mt-1">${(project.spent || 0).toLocaleString()}</p>
            </CardContent>
          </Card>
        )}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Status</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1 capitalize">{project.status}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tasks Section */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-zinc-50 font-['Manrope']">Tasks</CardTitle>
          {(user?.role === "admin" || user?.role === "team" || user?.role === "pm" || user?.role === "pm_lead") && (
            <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="create-task-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  New Task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="text-zinc-50 font-['Manrope']">Create New Task</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateTask} className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Task Name</Label>
                      <Input
                        value={taskForm.name}
                        onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                        required
                        className="bg-zinc-950 border-zinc-800 text-zinc-50"
                        data-testid="task-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300 flex items-center gap-2">
                        Type
                        <span className="relative group">
                          <Info className="h-4 w-4 text-zinc-500 cursor-help" />
                          <span className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 px-3 py-2 bg-zinc-800 text-xs text-zinc-300 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity z-50 pointer-events-none">
                            Select service type from settings
                          </span>
                        </span>
                      </Label>
                      <Select
                        value={taskForm.task_type}
                        onValueChange={(v) => {
                          const selectedType = serviceTypes.find(t => t.name === v);
                          setTaskForm({ 
                            ...taskForm, 
                            task_type: v,
                            service_type_id: selectedType?.id || ""
                          });
                        }}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50" data-testid="task-type-select">
                          <SelectValue placeholder="Select type..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                          {serviceTypes.map((t) => (
                            <SelectItem key={t.id} value={t.name} className="text-zinc-300">
                              {t.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Description</Label>
                    <Textarea
                      value={taskForm.description}
                      onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[80px]"
                      data-testid="task-description-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Brief / Requirements</Label>
                    <Textarea
                      value={taskForm.brief}
                      onChange={(e) => setTaskForm({ ...taskForm, brief: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50 min-h-[100px]"
                      placeholder="Detailed instructions for the artist..."
                      data-testid="task-brief-input"
                    />
                  </div>
                  
                  {/* Brief Link (TZ) */}
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Brief / TZ Link</Label>
                    <Input
                      type="url"
                      value={taskForm.brief_link}
                      onChange={(e) => setTaskForm({ ...taskForm, brief_link: e.target.value })}
                      placeholder="https://drive.google.com/... or Figma link"
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                      data-testid="task-brief-link-input"
                    />
                    <p className="text-xs text-zinc-500">Link to technical specification document</p>
                  </div>
                  
                  {/* Pricing Section */}
                  <div className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-medium text-zinc-400 uppercase">Pricing Calculator</h4>
                      {/* Currency Selector */}
                      <Select
                        value={taskForm.currency}
                        onValueChange={(v) => setTaskForm({ ...taskForm, currency: v })}
                      >
                        <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50 w-28">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-900 border-zinc-800">
                          <SelectItem value="USD">USD ($)</SelectItem>
                          <SelectItem value="EUR">EUR (€)</SelectItem>
                          <SelectItem value="UAH">UAH (₴)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Client Price ({taskForm.currency})</Label>
                        <Input
                          type="number"
                          value={taskForm.client_price || ""}
                          onChange={(e) => setTaskForm({ ...taskForm, client_price: parseFloat(e.target.value) || 0 })}
                          placeholder="Price charged to client"
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          data-testid="task-client-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-zinc-300">Artist Price ({taskForm.currency})</Label>
                        <Input
                          type="number"
                          value={taskForm.agreed_price || ""}
                          onChange={(e) => setTaskForm({ ...taskForm, agreed_price: parseFloat(e.target.value) || 0 })}
                          placeholder="Price paid to artist"
                          className="bg-zinc-950 border-zinc-800 text-zinc-50"
                          data-testid="task-price-input"
                        />
                      </div>
                    </div>
                    
                    {/* Art Director Review */}
                    <div className="p-3 border border-zinc-700 rounded-lg space-y-3">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="art_director_review"
                          checked={taskForm.art_director_review}
                          onChange={(e) => setTaskForm({ 
                            ...taskForm, 
                            art_director_review: e.target.checked,
                            art_director_id: e.target.checked ? taskForm.art_director_id : ""
                          })}
                          className="w-4 h-4 accent-orange-500"
                        />
                        <Label htmlFor="art_director_review" className="text-zinc-300 cursor-pointer">
                          Art Director Review (+30% artist cost)
                        </Label>
                      </div>
                      
                      {taskForm.art_director_review && (
                        <div className="space-y-2">
                          <Label className="text-zinc-400 text-sm">Select Art Director</Label>
                          <Select
                            value={taskForm.art_director_id}
                            onValueChange={(v) => setTaskForm({ ...taskForm, art_director_id: v })}
                          >
                            <SelectTrigger className="bg-zinc-950 border-zinc-800 text-zinc-50">
                              <SelectValue placeholder="Choose art director..." />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-900 border-zinc-800">
                              {artDirectors.map(ad => (
                                <SelectItem key={ad.id} value={ad.id}>{ad.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {taskForm.art_director_id && taskForm.agreed_price > 0 && (
                            <p className="text-xs text-orange-400">
                              Art Director fee: {taskForm.currency === "USD" ? "$" : taskForm.currency === "EUR" ? "€" : "₴"}{(taskForm.agreed_price * 0.3).toFixed(0)} (30% of artist price)
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                    
                    {/* Suggested Price & Margin Indicators */}
                    {taskForm.client_price > 0 && (
                      <div className="grid grid-cols-3 gap-4 pt-2">
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-zinc-500 uppercase mb-1">Suggested Price</p>
                          <p className="text-lg font-mono text-green-400">${pricing.suggestedPrice.toFixed(0)}</p>
                          <p className="text-xs text-zinc-500">Max 35% of client</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-zinc-500 uppercase mb-1">Artist Share</p>
                          <p className={cn(
                            "text-lg font-mono",
                            pricing.marginStatus === "OK" && "text-green-400",
                            pricing.marginStatus === "WARNING" && "text-yellow-400",
                            pricing.marginStatus === "CRITICAL" && "text-red-400"
                          )}>
                            {pricing.artistSharePercent.toFixed(1)}%
                          </p>
                          <p className="text-xs text-zinc-500">of client price</p>
                        </div>
                        <div className="p-3 bg-zinc-800/50 rounded-lg">
                          <p className="text-xs text-zinc-500 uppercase mb-1">Margin Status</p>
                          <div className={cn(
                            "flex items-center gap-2 text-lg font-medium",
                            pricing.marginStatus === "OK" && "text-green-400",
                            pricing.marginStatus === "WARNING" && "text-yellow-400",
                            pricing.marginStatus === "CRITICAL" && "text-red-400"
                          )}>
                            {pricing.marginStatus === "OK" && <CheckCircle2 className="h-5 w-5" />}
                            {pricing.marginStatus === "WARNING" && <AlertTriangle className="h-5 w-5" />}
                            {pricing.marginStatus === "CRITICAL" && <AlertTriangle className="h-5 w-5" />}
                            {pricing.marginStatus}
                          </div>
                          <p className="text-xs text-zinc-500">
                            {pricing.marginStatus === "OK" && "≤35%"}
                            {pricing.marginStatus === "WARNING" && "35-45%"}
                            {pricing.marginStatus === "CRITICAL" && ">45%"}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Auto-fill suggested price button */}
                    {taskForm.client_price > 0 && taskForm.agreed_price !== pricing.suggestedPrice && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setTaskForm({ ...taskForm, agreed_price: Math.round(pricing.suggestedPrice) })}
                        className="border-green-700 text-green-400 hover:bg-green-900/30"
                      >
                        Use Suggested Price (${pricing.suggestedPrice.toFixed(0)})
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Deadline</Label>
                      <Input
                        type="date"
                        value={taskForm.deadline}
                        onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })}
                        className="bg-zinc-950 border-zinc-800 text-zinc-50"
                        data-testid="task-deadline-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Assign Artist (Optional)</Label>
                      <ArtistSelector
                        selectedArtistId={taskForm.assigned_artist_id}
                        onSelect={(artistId, artistData) => {
                          setTaskForm({ ...taskForm, assigned_artist_id: artistId });
                          setSelectedArtistData(artistData);
                        }}
                        taskType={taskForm.task_type}
                      />
                      
                      {/* Price Comparison Block - shows after artist selection */}
                      {selectedArtistData && taskForm.client_price > 0 && (
                        <div className="mt-2 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700 space-y-2">
                          <p className="text-xs text-zinc-400 font-medium">Price Comparison</p>
                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="p-2 bg-zinc-900/50 rounded">
                              <p className="text-[10px] text-zinc-500 uppercase">Client pays</p>
                              <p className="text-sm font-mono text-zinc-50">${taskForm.client_price}</p>
                            </div>
                            <div className="p-2 bg-zinc-900/50 rounded">
                              <p className="text-[10px] text-zinc-500 uppercase">System suggests</p>
                              <p className="text-sm font-mono text-green-400">${Math.round(pricing.suggestedPrice)}</p>
                            </div>
                            <div className="p-2 bg-zinc-900/50 rounded">
                              <p className="text-[10px] text-zinc-500 uppercase">Artist wants</p>
                              <p className={cn(
                                "text-sm font-mono",
                                selectedArtistData.desired_rates?.[taskForm.task_type] 
                                  ? "text-yellow-400" 
                                  : "text-zinc-500"
                              )}>
                                {selectedArtistData.desired_rates?.[taskForm.task_type] 
                                  ? `$${selectedArtistData.desired_rates[taskForm.task_type]}`
                                  : "—"
                                }
                              </p>
                            </div>
                          </div>
                          {/* Quick hint */}
                          {selectedArtistData.desired_rates?.[taskForm.task_type] && (
                            <p className={cn(
                              "text-[10px] text-center pt-1",
                              selectedArtistData.desired_rates[taskForm.task_type] <= pricing.suggestedPrice
                                ? "text-green-400"
                                : selectedArtistData.desired_rates[taskForm.task_type] <= taskForm.client_price * 0.45
                                  ? "text-yellow-400"
                                  : "text-red-400"
                            )}>
                              {selectedArtistData.desired_rates[taskForm.task_type] <= pricing.suggestedPrice
                                ? "✓ Artist rate within budget"
                                : selectedArtistData.desired_rates[taskForm.task_type] <= taskForm.client_price * 0.45
                                  ? "⚠ Artist rate above suggested, but acceptable"
                                  : "⚠ Artist rate exceeds margin threshold"
                              }
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Internal Notes</Label>
                    <Textarea
                      value={taskForm.internal_notes}
                      onChange={(e) => setTaskForm({ ...taskForm, internal_notes: e.target.value })}
                      className="bg-zinc-950 border-zinc-800 text-zinc-50"
                      placeholder="Notes visible only to the team..."
                      data-testid="task-notes-input"
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-4">
                    <Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)} className="border-zinc-700">
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      disabled={pricing.marginStatus === "CRITICAL"}
                      className={pricing.marginStatus === "CRITICAL" ? "bg-zinc-600 cursor-not-allowed" : "bg-orange-500 hover:bg-orange-600"} 
                      data-testid="task-submit-btn"
                    >
                      {pricing.marginStatus === "CRITICAL" ? "Margin Too Low" : "Create Task"}
                    </Button>
                  </div>
                  {pricing.marginStatus === "CRITICAL" && (
                    <p className="text-sm text-red-400 text-center">
                      Cannot create task: Artist share exceeds {marginSettings.critical_threshold_percent}% (critical threshold)
                    </p>
                  )}
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent>
          {tasks.length === 0 ? (
            <div className="text-center py-12 text-zinc-500">
              <p>No tasks yet</p>
              {(user?.role === "admin" || user?.role === "team") && (
                <p className="text-sm mt-1">Create your first task to get started</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 text-xs py-2">Task</TableHead>
                  <TableHead className="text-zinc-400 text-xs py-2">Artist</TableHead>
                  <TableHead className="text-zinc-400 text-xs py-2 text-center">Versions</TableHead>
                  <TableHead className="text-zinc-400 text-xs py-2">Progress</TableHead>
                  {user?.role !== "client" && !isArtDirector && (
                    <TableHead className="text-zinc-400 text-xs py-2 text-right">Price</TableHead>
                  )}
                  {isArtDirector && (
                    <TableHead className="text-zinc-400 text-xs py-2 text-right">AD Fee</TableHead>
                  )}
                  <TableHead className="text-zinc-400 text-xs py-2">Deadline</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => {
                  const timeline = taskTimelines[task.id];
                  const versions = task.versions || [];
                  const totalVersions = versions.length;
                  const revisions = versions.filter(v => 
                    v.internal_status === 'rejected' || 
                    (v.client_approved === false && v.internal_status === 'approved')
                  ).length;
                  const currentVersion = totalVersions > 0 ? versions[totalVersions - 1] : null;
                  const currentStatus = currentVersion?.internal_status;
                  
                  return (
                    <TableRow 
                      key={task.id} 
                      className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                      onClick={() => navigate(`/tasks/${task.id}`)}
                      data-testid={`task-row-${task.id}`}
                    >
                      <TableCell className="py-2">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-zinc-50 text-sm font-medium hover:text-orange-500 transition-colors">
                            {task.name}
                          </span>
                          <Badge variant="outline" className="bg-zinc-900/50 text-zinc-500 border-zinc-700 text-[10px] w-fit capitalize">
                            {task.task_type.replace("_", " ")}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-sm text-zinc-400">
                        {task.assigned_artist_name || <span className="text-zinc-600">Unassigned</span>}
                      </TableCell>
                      <TableCell className="py-2 text-center">
                        {totalVersions === 0 ? (
                          <span className="text-xs text-zinc-600">—</span>
                        ) : (
                          <div className="flex flex-col items-center gap-0.5">
                            <span className="text-xs text-zinc-50 font-medium">v{totalVersions}</span>
                            {revisions > 0 && (
                              <span className="text-[10px] text-orange-400">{revisions} rev</span>
                            )}
                            {currentStatus && (
                              <Badge className={cn(
                                "text-[9px] px-1 py-0",
                                currentStatus === 'approved' ? "bg-green-900/50 text-green-400" :
                                currentStatus === 'rejected' ? "bg-red-900/50 text-red-400" :
                                "bg-yellow-900/50 text-yellow-400"
                              )}>
                                {currentStatus === 'pending' ? 'review' : currentStatus}
                              </Badge>
                            )}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="py-2 min-w-[140px]">
                        {timeline ? (
                          <TaskTimeline
                            stages={timeline.stages}
                            currentStageIndex={timeline.current_stage_index || 0}
                            showLabels={false}
                            size="sm"
                          />
                        ) : (
                          <span className="text-xs text-zinc-600">—</span>
                        )}
                      </TableCell>
                      {user?.role !== "client" && !isArtDirector && (
                        <TableCell className="py-2 text-right">
                          {task.agreed_price > 0 ? (
                            <span className="text-sm font-mono text-zinc-400">${task.agreed_price}</span>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </TableCell>
                      )}
                      {isArtDirector && (
                        <TableCell className="py-2 text-right">
                          {task.art_director_fee > 0 ? (
                            <span className="text-sm font-mono text-purple-400">${task.art_director_fee}</span>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="py-2 text-xs text-zinc-400">
                        {task.deadline_phases && task.deadline_phases.length > 0 ? (
                          <DeadlinePhasesBadge phases={task.deadline_phases} />
                        ) : (
                          task.deadline ? new Date(task.deadline).toLocaleDateString() : "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectDetailPage;
